import { GUIDE_PARAM, GUIDES_VIEW, QUERY_PARAM, VIEW_PARAM } from "../insights-links";

/**
 * The browser half of the Guides view (D181 of documentation/pr-plans/insights-simplify-plan.md;
 * sites/insights/guides/pane.blade.php): which of the hub, the search results and the guides shows; the search
 * over the basics and the guides' sections, whose results take the hub's place while the box holds two letters
 * or more, so the hub never reflows as a person types; the basics' answers, each a disclosure; the section a link
 * lands on, tinted for a moment; and the three calculators, which run the app's own figures (InsightsGuides::figures()).
 *
 * The page's views script opens the view and names the guide (insights-views.js, OPEN_GUIDE_EVENT); every link
 * inside the pane is a plain address into the page, which that script opens in place as one history entry, so
 * Back and Forward move through the guides as through the activities. The search is written into the address as
 * it changes (?q=), replaced, never pushed: a reload opens on the same results, and Back leaves the view.
 * Prefix: g_.
 *
 * @param {object} config
 * @param {string} config.guide   The guide the page opened on, or "hub" (InsightsGuides::guideOrHub()).
 * @param {string} config.query   What the search box opened with (?q=).
 * @param {Array<{kind: string, title: string, text: string, href: string, haystack: string}>} config.index  InsightsGuides::searchIndex().
 * @param {object} config.figures  InsightsGuides::figures(): weights, rates, points, max.
 * @param {object} config.words    The sentences the browser fills (guides/pane.blade.php), their slots marked.
 */
export default function InsightsGuides({ guide = "hub", query = "", index = [], figures = {}, words = {} }) {
    const weights = figures.weights ?? { top: 0.04, middle: 0.5, bottom: 1 };
    const rates = figures.rates ?? {};
    const positions = Object.keys(rates).map(Number);
    const lastPosition = positions.length > 0 ? Math.max(...positions) : 20;

    return {
        g_guide: guide || "hub",
        g_query: query || "",
        g_results: [],
        // The basics answer open on the hub, by its id.
        g_open: null,
        g_onOpen: null,
        g_targetTimer: null,

        // The calculators' inputs: the site's own funnel to start with, and the first row's figures for the others.
        calc: { top: 2140, middle: 844, bottom: 703, impressions: 930, position: 6, clicks: 35, page: 15, difficulty: 12, value: 10, visitors: 10 },

        init() {
            this.g_onOpen = (event) => this.g_opened(event.detail ?? {});
            window.addEventListener("insights-open-guide", this.g_onOpen);

            if (this.g_query.trim().length >= 2) {
                this.g_search(false);
            }

            this.$watch("g_query", () => this.g_search(true));

            const section = window.location.hash.replace(/^#/, "");

            if (section !== "") {
                this.$nextTick(() => this.g_jump(section));
            }
        },

        destroy() {
            window.removeEventListener("insights-open-guide", this.g_onOpen);
            clearTimeout(this.g_targetTimer);
        },

        /** A link into the view, or Back and Forward: the guide it names shows, at its section. */
        g_opened({ guide: next = null, section = null, query: q = null }) {
            if (q !== null && q.trim().length >= 2) {
                this.g_query = q;
            } else {
                this.g_query = "";
                this.g_guide = next || "hub";
            }

            if (section !== null) {
                this.$nextTick(() => this.g_jump(section));
            }
        },

        /**
         * The results for what the box holds, from two letters: every basics answer and guide section whose words
         * hold it, basics first. Under two letters the hub, or the guide the search left, shows again.
         */
        g_search(write = true) {
            const q = this.g_query.trim().toLowerCase();

            if (q.length < 2) {
                this.g_results = [];

                if (this.g_guide === "search") {
                    this.g_guide = "hub";
                }
            } else {
                this.g_results = index.filter((entry) => entry.haystack.includes(q));
                this.g_guide = "search";
            }

            if (write) {
                this.g_writeQuery(q.length < 2 ? "" : this.g_query.trim());
            }
        },

        /** ?q= replaced in the address while the view shows, so a reload opens on the same results. */
        g_writeQuery(q) {
            const url = new URL(window.location.href);

            if (url.searchParams.get(VIEW_PARAM) !== GUIDES_VIEW) {
                return;
            }

            if (q === "") {
                url.searchParams.delete(QUERY_PARAM);
            } else {
                url.searchParams.set(QUERY_PARAM, q);
                url.searchParams.delete(GUIDE_PARAM);
                url.hash = "";
            }

            if (url.href !== window.location.href) {
                window.history.replaceState(window.history.state, "", url);
            }
        },

        g_clear() {
            this.g_query = "";
            this.$nextTick(() => this.$refs.gSearch?.focus());
        },

        /** The number of results, said once per search (the status region). */
        g_count() {
            return this.g_results.length;
        },

        g_countText() {
            const count = this.g_count();
            const q = this.g_query.trim();
            const template = count === 0 ? words.count?.zero : count === 1 ? words.count?.one : words.count?.many;

            return String(template ?? "").replace("__N__", String(count)).replace("__Q__", q);
        },

        g_toggle(id) {
            this.g_open = this.g_open === id ? null : id;
        },

        /**
         * The section a link named, brought under the pinned bar and tinted for a moment; a basics answer's id
         * opens that answer. The elements' own scroll margin keeps them clear of the bar (2.4.11).
         */
        g_jump(section) {
            const element = document.getElementById(section);

            if (!element) {
                return;
            }

            if (section.startsWith("basics-")) {
                this.g_open = section.slice("basics-".length);
            }

            element.scrollIntoView({ block: "start" });
            element.classList.add("is-target");
            clearTimeout(this.g_targetTimer);
            this.g_targetTimer = setTimeout(() => element.classList.remove("is-target"), 2500);

            const heading = element.matches("[tabindex]") ? element : element.querySelector("[tabindex='-1'], button");

            heading?.focus({ preventScroll: true });
        },

        /* ---- what your clicks are worth: each search intent's clicks times its weight (FunnelWords) ---- */

        g_worth(intent) {
            return clicksWorth(num(this.calc[intent]), weights[intent]);
        },

        g_worthTotal() {
            return ["top", "middle", "bottom"].reduce((total, intent) => total + clicksWorth(num(this.calc[intent]), weights[intent]), 0);
        },

        /* ---- Potential clicks: the usual clicks at position 3, less the usual where the page is (PotentialClicks) ---- */

        g_potential() {
            return potentialClicks(num(this.calc.impressions), num(this.calc.position, 1), rates, lastPosition);
        },

        /** The figure as the lists print it: "+29.4 a month", that the climb is done, or that the table ends before the position. */
        g_potentialText() {
            const { gain, beyond } = this.g_potential();

            if (beyond) {
                return String(words.beyond ?? "");
            }

            return gain > 0 ? String(words.a_month ?? "+__N__").replace("__N__", this.g_fmt(gain, 1)) : String(words.top3 ?? "");
        },

        /** The sum, written out, under the figure. */
        g_potentialNote() {
            const { now, then, gain, beyond } = this.g_potential();
            const impressions = num(this.calc.impressions);
            const position = Math.min(lastPosition, Math.max(1, Math.round(num(this.calc.position, 1))));

            if (beyond) {
                return String(words.beyond_note ?? "");
            }

            if (gain <= 0) {
                return String(words.top3_note ?? "");
            }

            const percent = (clicks) => (impressions > 0 ? ((clicks / impressions) * 100).toFixed(2) : "0.00");

            return String(words.formula ?? "")
                .replace("__I__", this.g_fmt(impressions))
                .replace("__T__", percent(then))
                .replace("__W__", percent(now))
                .replace("__P__", String(position))
                .replace("__G__", this.g_fmt(gain, 1));
        },

        /* ---- the score: the points of the step each check reaches (OpportunityPoints::sum()) ---- */

        g_score() {
            return scoreOf([this.calc.clicks, this.calc.page, this.calc.difficulty, this.calc.value, this.calc.visitors].map((part) => num(part)));
        },

        g_scoreParts() {
            return [this.calc.clicks, this.calc.page, this.calc.difficulty, this.calc.value, this.calc.visitors].map((part) => num(part));
        },

        /** A number as the guides print one: whole, with thousands separated; one decimal where asked. */
        g_fmt(value, decimals = 0) {
            return Number(value).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
        },
    };
}

function num(value, fallback = 0) {
    const n = Number(value);

    return value === "" || value === null || value === undefined || Number.isNaN(n) ? fallback : n;
}

/** What `clicks` from a search intent count as, in clicks from a buyer: the intent's weight times the clicks. */
export function clicksWorth(clicks, weight) {
    return Math.max(0, clicks) * weight;
}

/**
 * Potential clicks for a search (PotentialClicks::forQuery()): what a page at position 3 usually gets from the
 * impressions, less what a page usually gets at the position, both from the click-rate table; 0 at position 3
 * or better, where the climb is done. Positions past the table's last take its rate.
 *
 * @returns {{now: number, then: number, gain: number}}
 */
export function potentialClicks(impressions, position, rates, lastPosition) {
    // A position past the table, off Google's first two pages, has no usual clicks and no figure, as the lists
    // give none (PotentialClicks::gains() ends with the table); the top 3 have nothing to gain.
    const rounded = Math.max(1, Math.round(position));
    const beyond = rounded > lastPosition;
    const rateAt = (at) => rates[Math.min(lastPosition, Math.max(1, at))] ?? 0;
    const now = beyond ? 0 : Math.max(0, impressions) * rateAt(rounded);
    const then = Math.max(0, impressions) * rateAt(3);

    return { now, then, gain: rounded > 3 && !beyond ? then - now : 0, beyond };
}

/** The score: the parts' points added up (OpportunityPoints::sum()). */
export function scoreOf(parts) {
    return parts.reduce((total, part) => total + part, 0);
}
